/**
 * Modern Sidebar Component
 * 
 * A modern, collapsible navigation sidebar with Material Design 3 principles
 */

import React, { useState } from 'react';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Divider,
  Collapse,
  Typography,
  Tooltip,
  Avatar,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Menu as MenuIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  ExpandLess,
  ExpandMore,
  Dashboard,
  Settings,
  Layers,
  ViewInAr,
  Brush,
  CameraAlt,
  VolumeUp,
  TableChart,
  Assessment,
  Help,
  Person,
} from '@mui/icons-material';

interface SidebarProps {
  open?: boolean;
  onToggle?: () => void;
  selectedTool?: string;
  onToolSelect?: (tool: string) => void;
}

interface MenuItem {
  id: string;
  label: string;
  icon: React.ReactElement;
  children?: MenuItem[];
  badge?: number;
}

const ModernSidebar: React.FC<SidebarProps> = ({
  open = true,
  onToggle,
  selectedTool = 'dashboard',
  onToolSelect,
}) => {
  const theme = useTheme();
  const [expanded, setExpanded] = useState<{ [key: string]: boolean }>({});

  const drawerWidth = open ? 280 : 72;

  const menuItems: MenuItem[] = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: <Dashboard />,
    },
    {
      id: 'room-design',
      label: 'Room Design',
      icon: <ViewInAr />,
      children: [
        { id: 'floorplan', label: 'Floor Plan', icon: <Layers /> },
        { id: 'furniture', label: 'Furniture', icon: <TableChart /> },
        { id: 'walls', label: 'Walls & Doors', icon: <ViewInAr /> },
      ],
    },
    {
      id: 'devices',
      label: 'Devices',
      icon: <VolumeUp />,
      children: [
        { id: 'speakers', label: 'Speakers', icon: <VolumeUp /> },
        { id: 'microphones', label: 'Microphones', icon: <VolumeUp /> },
        { id: 'cameras', label: 'Cameras', icon: <CameraAlt /> },
      ],
    },
    {
      id: 'visualization',
      label: 'Visualization',
      icon: <Brush />,
      children: [
        { id: 'coverage', label: 'Coverage Map', icon: <Layers /> },
        { id: 'heatmap', label: 'Heat Map', icon: <Assessment /> },
        { id: '3d-view', label: '3D View', icon: <ViewInAr /> },
      ],
    },
    {
      id: 'analysis',
      label: 'Analysis',
      icon: <Assessment />,
      badge: 2,
    },
  ];

  const bottomMenuItems: MenuItem[] = [
    {
      id: 'help',
      label: 'Help & Support',
      icon: <Help />,
    },
    {
      id: 'settings',
      label: 'Settings',
      icon: <Settings />,
    },
    {
      id: 'profile',
      label: 'Profile',
      icon: <Person />,
    },
  ];

  const handleExpand = (id: string) => {
    setExpanded((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const renderMenuItem = (item: MenuItem, depth = 0) => {
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expanded[item.id];
    const isSelected = selectedTool === item.id;

    return (
      <React.Fragment key={item.id}>
        <ListItem disablePadding sx={{ display: 'block' }}>
          <Tooltip title={!open ? item.label : ''} placement="right">
            <ListItemButton
              onClick={() => {
                if (hasChildren) {
                  handleExpand(item.id);
                } else {
                  onToolSelect?.(item.id);
                }
              }}
              selected={isSelected}
              sx={{
                minHeight: 48,
                justifyContent: open ? 'initial' : 'center',
                px: 2.5,
                pl: depth > 0 ? 4 : 2.5,
                borderRadius: 2,
                mx: 1,
                my: 0.5,
                backgroundColor: isSelected
                  ? alpha(theme.palette.primary.main, 0.12)
                  : 'transparent',
                '&:hover': {
                  backgroundColor: isSelected
                    ? alpha(theme.palette.primary.main, 0.16)
                    : alpha(theme.palette.action.hover, 0.08),
                },
                '&.Mui-selected': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.12),
                  borderLeft: `3px solid ${theme.palette.primary.main}`,
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.16),
                  },
                },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: open ? 3 : 'auto',
                  justifyContent: 'center',
                  color: isSelected ? theme.palette.primary.main : 'inherit',
                }}
              >
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={item.label}
                sx={{
                  opacity: open ? 1 : 0,
                  color: isSelected ? theme.palette.primary.main : 'inherit',
                }}
              />
              {open && hasChildren && (
                isExpanded ? <ExpandLess /> : <ExpandMore />
              )}
              {open && item.badge && (
                <Box
                  component="span"
                  sx={{
                    backgroundColor: theme.palette.error.main,
                    color: 'white',
                    borderRadius: '12px',
                    padding: '2px 8px',
                    fontSize: '0.75rem',
                    minWidth: '20px',
                    textAlign: 'center',
                  }}
                >
                  {item.badge}
                </Box>
              )}
            </ListItemButton>
          </Tooltip>
        </ListItem>
        {hasChildren && (
          <Collapse in={isExpanded && open} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {item.children!.map((child) => renderMenuItem(child, depth + 1))}
            </List>
          </Collapse>
        )}
      </React.Fragment>
    );
  };

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          boxSizing: 'border-box',
          backgroundColor: theme.palette.background.paper,
          borderRight: `1px solid ${theme.palette.divider}`,
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
          overflowX: 'hidden',
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: open ? 'space-between' : 'center',
          p: 2,
          minHeight: 64,
        }}
      >
        {open && (
          <Typography variant="h6" fontWeight="bold" color="primary">
            Room Designer
          </Typography>
        )}
        <IconButton onClick={onToggle} size="small">
          {open ? <ChevronLeftIcon /> : <MenuIcon />}
        </IconButton>
      </Box>

      <Divider />

      {/* User Profile Section (when open) */}
      {open && (
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar
              sx={{
                width: 40,
                height: 40,
                bgcolor: theme.palette.primary.main,
              }}
            >
              JD
            </Avatar>
            <Box>
              <Typography variant="subtitle2" fontWeight="bold">
                John Doe
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Design Engineer
              </Typography>
            </Box>
          </Box>
        </Box>
      )}

      <Divider />

      {/* Main Menu */}
      <Box sx={{ flexGrow: 1, overflow: 'auto', py: 1 }}>
        <List>
          {menuItems.map((item) => renderMenuItem(item))}
        </List>
      </Box>

      <Divider />

      {/* Bottom Menu */}
      <Box sx={{ pb: 2 }}>
        <List>
          {bottomMenuItems.map((item) => renderMenuItem(item))}
        </List>
      </Box>
    </Drawer>
  );
};

export default ModernSidebar;